import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    useEffect,
} from 'react'
import './SimulateNew.css'
import { useHistory } from 'react-router'
import ProgramHeaderTwo from '../ReusableComponents/Header/ProgramHeaderTwo'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import MakeContext from '../Make/MakeContext'
import ContentEngine from '../ContentEngine/ContentEngine'

import {
    MdOutlineArrowBack,
    MdOutlineViewInAr,
    MdFullscreen,
    MdClosedCaption,
} from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { FaShapes } from 'react-icons/fa6'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { RxReset } from 'react-icons/rx'

import getBackendImage from '../../source/imageUtil'

// CONSTANTS
const BACKEND_URL = process.env.REACT_APP_API_BASE
// const PROJECT_ID = 'talking-dragon' // Now parsed from URL inside the component

const SimulateNew = ({
    data: dataProp,
    projectId: propProjectId,
    screenId: propScreenId,
    initialStep,
}) => {
    const history = useHistory()

    // --- NEW: Parse URL inside the component so it updates on route change ---
    const urlParams = useMemo(
        () =>
            new URLSearchParams(
                history.location?.search || window.location.search
            ),
        [history.location?.search]
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const PROJECT_ID =
        propProjectId || urlParams.get('projectId') || null
    const screenId = propScreenId || urlParams.get('screenId') || null

    const makeCtx = useContext(MakeContext)
    const isMake = !!makeCtx
    const { setHeaderProps, setFooterProps, setToolbarProps } = makeCtx || {}
    const isShellMode = isMake && !!setHeaderProps

    const HeaderComponent = MakeHeader
    const audioRef = useRef(null)
    const appContainerRef = useRef(null)
    const modelViewerRef = useRef(null)
    const isModelLoadedRef = useRef(false)

    // STATE
    const storageKey = `simulate_step_${PROJECT_ID}_${screenId || 'default'}`
    const [transcript, setTranscript] = useState(null)
    const [loading, setLoading] = useState(true)

    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1

        // PRIORITY 1: Explicit jump from Timeline
        if (initialStep) {
            sessionStorage.setItem(storageKey, initialStep.toString())
            return initialStep
        }

        // PRIORITY 2: Memory from last visit
        const savedStep = sessionStorage.getItem(storageKey)
        const parsed = parseInt(savedStep, 10)
        return !isNaN(parsed) && parsed > 0 ? parsed : 1
    })
    // const [showSubtitles, setShowSubtitles] = useState(false)
    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )
    const [showMaterials, setShowMaterials] = useState(
        () => sessionStorage.getItem('materialsToggle') !== 'false'
    )

    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!transcript?.meta?.audioBaseUrl) return

            const audioUrl = `${transcript.meta.audioBaseUrl}${stepNumber}.mp3?t=${Date.now()}`

            // Make mode: play through the shell's persistent, pre-unlocked
            // audio element so playback survives cross-screen navigation.
            if (makeCtx?.playAudio) {
                makeCtx.playAudio(audioUrl)
                return
            }

            if (!audioRef.current) return
            audioRef.current.src = audioUrl
            audioRef.current
                .play()
                .catch((e) => console.log('Playback interrupted or failed:', e))
        },
        [transcript, makeCtx]
    )

    // 1. FETCH DATA ON MOUNT (skip if data provided via props)
    useEffect(() => {
        if (dataProp) {
            setTranscript(dataProp)
            setLoading(false)
            return
        }
        const fetchTranscript = async () => {
            try {
                // --- NEW: Dynamically select Staging or Production API ---
                let endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/simulate/${PROJECT_ID}`
                    : `${BACKEND_URL}/api/simulate/${PROJECT_ID}`
                if (screenId) endpoint += `/${screenId}`

                const res = isPreviewMode
                    ? await fetch(endpoint, { credentials: 'include' })
                    : await fetch(endpoint)
                if (!res.ok) throw new Error('Failed to fetch transcript')
                const data = await res.json()
                setTranscript(data)
                setLoading(false)
            } catch (error) {
                console.error('Error loading project:', error)
                setLoading(false)
            }
        }
        fetchTranscript()
    }, [dataProp])

    // useEffect(() => {
    //     if (!transcript || !transcript.steps?.length) return

    //     const savedStep = parseInt(
    //         sessionStorage.getItem('playdynamexTutorialStep'),
    //         10
    //     )

    //     if (!savedStep || savedStep < 1) {
    //         setCurrentStep(1)
    //         return
    //     }

    //     if (savedStep > transcript.steps.length) {
    //         setCurrentStep(transcript.steps.length)
    //         return
    //     }

    //     setCurrentStep(savedStep)
    // }, [transcript])

    // 2. TRIGGER AUDIO ONLY AFTER LOADING IS COMPLETE
    useEffect(() => {
        if (!loading && transcript && aiVoiceover) {
            playAudioForStep(currentStep)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, transcript]) // only on initial load

    // COMPUTED VALUES
    const totalSteps = useMemo(
        () => transcript?.steps?.length || transcript?.totalSteps || 0,
        [transcript]
    )

    // const modelPath = useMemo(() => {
    //     return '/models/drackganimation.glb'
    // }, [])
    const modelPath = useMemo(
        () => `${transcript?.meta?.modelPath}`,
        [transcript]
    )

    // 1. FIRST, find the data for the current step
    const currentStepData = useMemo(() => {
        if (!transcript) return null
        return transcript.steps.find((step) => step.stepNumber === currentStep)
    }, [currentStep, transcript])

    // 2. THEN, pull the actual animation name from that step
    const currentAnimationName = useMemo(() => {
        if (!currentStepData) return undefined

        // Look for the specific animation name we saved from the Admin Panel
        if (currentStepData.animationName) {
            return currentStepData.animationName
        }

        // Fallback for older projects that haven't been updated in the admin panel yet
        return currentStep.toString().padStart(2, '0')
    }, [currentStepData, currentStep])

    // Handle model load: set timeScale, mark model as ready, and kick off initial playback.
    useEffect(() => {
        if (loading) return
        const mv = modelViewerRef.current
        if (!mv) return

        isModelLoadedRef.current = false // reset for new model

        const handleLoad = () => {
            // Safely parse as Number, fallback strictly to 1.0 for older projects
            const targetSpeed =
                currentStepData?.speed !== undefined
                    ? Number(currentStepData.speed)
                    : 1.0
            mv.timeScale = targetSpeed > 0 ? targetSpeed : 1.0
            isModelLoadedRef.current = true
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const m = modelViewerRef.current
                    if (!m) return
                    m.currentTime = 0
                    m.play({ repetitions: 100 })
                })
            })
        }

        mv.addEventListener('load', handleLoad)
        return () => mv.removeEventListener('load', handleLoad)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, modelPath]) // currentStepData intentionally omitted

    // Play once and freeze on the last frame whenever the step (animation) changes.
    useEffect(() => {
        const mv = modelViewerRef.current
        if (!mv || !currentAnimationName || !isModelLoadedRef.current) return

        let raf1, raf2
        let isCancelled = false

        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (isCancelled) return
                const m = modelViewerRef.current
                if (!m) return
                m.currentTime = 0

                // Read speed from current step data, default strictly to 1.0 if missing
                const targetSpeed =
                    currentStepData?.speed !== undefined
                        ? Number(currentStepData.speed)
                        : 1.0
                m.timeScale = targetSpeed > 0 ? targetSpeed : 1.0

                m.play({ repetitions: 1 })
            })
        })

        return () => {
            isCancelled = true
            cancelAnimationFrame(raf1)
            cancelAnimationFrame(raf2)
        }
    }, [currentAnimationName, currentStepData])

    // Floating subtitle state
    const [showSubtitleDisplay, setShowSubtitleDisplay] = useState(false)
    const [subtitleVisible, setSubtitleVisible] = useState(false)
    const [typewriterText, setTypewriterText] = useState('')
    const subtitleRef = useRef(null)
    const [aiSpeaking, setAiSpeaking] = useState(true)

    // Refined Typewriter Effect
    useEffect(() => {
        if (aiVoiceover && currentStepData?.instruction) {
            setTypewriterText('')
            setAiSpeaking(true) // AI is now "active"

            let i = 0
            const text = currentStepData.instruction
            const interval = setInterval(() => {
                i++
                setTypewriterText(text.slice(0, i))

                if (i >= text.length) {
                    clearInterval(interval)
                    // Keep aiSpeaking true if the audio is still playing,
                    // or set to false if you just want it to represent the text animation
                    setAiSpeaking(true)
                }
            }, 40) // 40ms is usually the sweet spot for readability
            return () => clearInterval(interval)
        } else {
            setTypewriterText(currentStepData?.instruction || '')
            setAiSpeaking(false)
        }
    }, [aiVoiceover, currentStepData])

    // Hide subtitle display on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (
                subtitleRef.current &&
                !subtitleRef.current.contains(e.target)
            ) {
                setShowSubtitleDisplay(false)
                setAiSpeaking(true)
            }
        }
        if (showSubtitleDisplay) {
            document.addEventListener('mousedown', handleClick)
        }
        return () => {
            document.removeEventListener('mousedown', handleClick)
        }
    }, [showSubtitleDisplay])

    // Slide in subtitle on mount
    useEffect(() => {
        const t = setTimeout(() => {
            setSubtitleVisible(true)
            setShowSubtitleDisplay(true)
        }, 50)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        sessionStorage.setItem(storageKey, currentStep.toString())
    }, [currentStep, storageKey])

    const resetTutorial = useCallback(() => {
        setCurrentStep(1)
        setShowSubtitleDisplay(true)
        setAiSpeaking(true)
        if (aiVoiceover) {
            playAudioForStep(1)
        }
    }, [aiVoiceover, playAudioForStep])

    const goBack = useCallback(() => {
        if (dataProp) {
            ContentEngine.prevToStart()
        } else {
            history.goBack()
        }
    }, [history, dataProp])

    // NAVIGATION HANDLERS
    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
            ContentEngine.tryEarnStep(globalStep)
            setCurrentStep(currentStep + 1)

            // Trigger Audio if Voiceover is ON
            if (aiVoiceover) {
                playAudioForStep(currentStep + 1)
            }
        } else {
            // Last step — earn it before navigating away
            const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
            ContentEngine.tryEarnStep(globalStep)

            if (isPreviewMode) return

            sessionStorage.removeItem(storageKey)
            if (dataProp) {
                ContentEngine.next()
            }
        }

        setAiSpeaking(false)
        setShowSubtitleDisplay(true)
    }, [
        currentStep,
        totalSteps,
        aiVoiceover,
        playAudioForStep,
        isPreviewMode,
        storageKey,
        dataProp,
    ])

    const handlePrev = useCallback(() => {
        if (currentStep > 1) {
            const prevStep = currentStep - 1
            setCurrentStep(prevStep)
            if (aiVoiceover) {
                playAudioForStep(prevStep)
            }
            setAiSpeaking(false)
            setShowSubtitleDisplay(true)
        } else if (dataProp) {
            // At step 1 in Make mode — go to the last step of the previous screen
            sessionStorage.removeItem(storageKey)
            ContentEngine.prev()
        }
    }, [currentStep, aiVoiceover, playAudioForStep, dataProp, storageKey])

    const handleFullscreen = useCallback(() => {
        // In Make mode, fullscreen the parent wrapper so it persists across screen transitions
        const el = isMake
            ? document.querySelector('.make-page')
            : appContainerRef.current

        const isFullscreen =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement

        if (!isFullscreen) {
            if (el?.requestFullscreen) el.requestFullscreen()
            else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen()
            else if (el?.msRequestFullscreen) el.msRequestFullscreen()
        } else {
            if (document.exitFullscreen) document.exitFullscreen()
            else if (document.webkitExitFullscreen)
                document.webkitExitFullscreen()
            else if (document.msExitFullscreen) document.msExitFullscreen()
        }
    }, [isMake])

    const handleHeaderTabClick = useCallback(
        (index) => {
            if (index > 3) {
                return
            }
            if (index === 0) {
                history.push('/playdynamexNew')
            }
            if (index === 1) {
                sessionStorage.setItem('connectedDevice', 'Toki')
                sessionStorage.setItem('deviceVersion', '1')
                history.push('/assemblyTwo')
            }
            if (index === 2) {
                history.push('/logicTwo')
            }
            if (index === 3) {
                history.push('/SimulateNew')
            }
        },
        [history]
    )

    // TOGGLE VOICEOVER
    const handleAiVoiceoverToggle = useCallback(() => {
        const nextState = !aiVoiceover
        setAiVoiceover(nextState)
        sessionStorage.setItem('aiVoiceToggle', String(nextState))

        if (nextState) {
            playAudioForStep(currentStep)
        } else {
            if (makeCtx?.stopAudio) {
                makeCtx.stopAudio()
            } else if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
            }
        }
    }, [aiVoiceover, currentStep, playAudioForStep, makeCtx])

    // TOGGLE MATERIALS PANEL
    const handleMaterialsToggle = useCallback(() => {
        setShowMaterials((prev) => {
            const nextState = !prev
            sessionStorage.setItem('materialsToggle', String(nextState))
            return nextState
        })
    }, [])

    // Stable onStepClick for timeline nav
    const onStepClick = useCallback(
        (stepNum) => {
            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
            if (aiVoiceover) playAudioForStep(stepNum)
            setAiSpeaking(false)
            setShowSubtitleDisplay(true)
        },
        [aiVoiceover, playAudioForStep]
    )

    // --- Shell: register header props ---
    useEffect(() => {
        if (!setHeaderProps) return
        setHeaderProps({
            title: transcript?.title || '',
            showJumpTo: false,
            titlePosition: 'left',
            help_btnPosition: 'right',
            isGoBack: true,
            goBack,
            showHelp: true,
            showBluetoothBtn: true,
            showUsbBtn: true,
            useUsbIcon: true,
            currentStep,
            totalSteps,
            stepTitles: transcript?.steps?.map((s) => s?.title || '') || [],
            onStepClick,
        })
    }, [
        setHeaderProps,
        transcript,
        currentStep,
        totalSteps,
        goBack,
        onStepClick,
    ])

    // --- Shell: register footer props ---
    useEffect(() => {
        if (!setFooterProps) return
        setFooterProps({
            stepCounter: `Step ${currentStep} of ${totalSteps}`,
            stepTitle: currentStepData?.title || '',
            nextDisabled: currentStep === totalSteps && !dataProp,
            nextLabel: currentStep === totalSteps ? 'Continue' : 'Next',
            onPrev: handlePrev,
            onNext: handleNext,
        })
    }, [
        setFooterProps,
        currentStep,
        totalSteps,
        currentStepData?.title,
        handlePrev,
        handleNext,
        dataProp,
    ])

    // --- Shell: register toolbar props ---
    useEffect(() => {
        if (!setToolbarProps) return
        setToolbarProps({
            aiVoiceover,
            onAiToggle: () => {
                handleAiVoiceoverToggle()
                setSubtitleVisible(true)
                setShowSubtitleDisplay(true)
            },
            showMaterials,
            onMaterialsToggle: handleMaterialsToggle,
            onFullscreen: handleFullscreen,
            onReset: resetTutorial,
        })
    }, [
        setToolbarProps,
        aiVoiceover,
        handleAiVoiceoverToggle,
        showMaterials,
        handleMaterialsToggle,
        handleFullscreen,
        resetTutorial,
    ])

    // RENDER LOADING STATE
    if (loading) {
        return (
            <div
                className="pdx-app-container"
                style={{
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                Loading Project...
            </div>
        )
    }

    if (!transcript) {
        return (
            <div className="pdx-app-container">Error loading project data.</div>
        )
    }

    return (
        <div className="pdx-app-container" ref={appContainerRef}>
            {/* --- NEW: Floating Exit Preview Button --- */}
            {isPreviewMode && (
                <button
                    onClick={() => history.goBack()}
                    style={{
                        position: 'fixed',
                        top: '12vh', // Just below your 10vh header
                        right: '30px',
                        zIndex: 9999,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}
            {/* HEADER — hidden in shell mode */}
            {!isShellMode && (
                <div className="pdx-header">
                    <HeaderComponent
                        showTimeline={isMake || isPreviewMode}
                        // title={isMake ? 'Beginners' : transcript.title}
                        title={transcript.title}
                        showJumpTo={!isMake && !isPreviewMode}
                        data={{
                            options: [
                                'Mechanical',
                                'Electronic',
                                'Coding',
                                'Simulate',
                            ],
                            selected: 3,
                            isCustomHeader: true,
                        }}
                        onJumpClick={handleHeaderTabClick}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showHelp={true}
                        showBluetoothBtn={true}
                        showUsbBtn={true}
                        useUsbIcon={true}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        stepTitles={
                            transcript?.steps?.map(
                                (step) => step?.title || ''
                            ) || []
                        }
                        onStepClick={(stepNum) => {
                            // stepNum is 1-based from ProgramTimelineNav
                            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
                            if (aiVoiceover) playAudioForStep(stepNum)
                            setAiSpeaking(false)
                            setShowSubtitleDisplay(true)
                        }}
                    />
                </div>
            )}

            {/* VIEWER */}
            <main className="pdx-viewer-container">
                {/* AI Speaking Pill / Subtitle Box — morphs between shapes */}
                {currentStepData?.instruction && (
                    <div
                        ref={subtitleRef}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: !subtitleVisible
                                ? 'translateX(-50%) translateY(-120vh)'
                                : 'translateX(-50%)',
                            maxWidth: !showSubtitleDisplay ? '280px' : '600px',
                            maxHeight: !showSubtitleDisplay ? '44px' : '150px',
                            borderRadius: !showSubtitleDisplay
                                ? '20px'
                                : '20px',
                            padding: !showSubtitleDisplay
                                ? '0 18px'
                                : '12px 24px',
                            background: !showSubtitleDisplay
                                ? 'rgba(60, 60, 120, 0.13)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            display: 'grid',
                            gridTemplateRows: !showSubtitleDisplay
                                ? '44px'
                                : 'auto',
                            alignItems: 'center',
                            justifyItems: 'center',
                            overflow: 'hidden',
                            zIndex: 101,
                            cursor: !showSubtitleDisplay
                                ? 'pointer'
                                : 'default',
                            opacity: !subtitleVisible ? 0 : 1,
                            pointerEvents: !subtitleVisible ? 'none' : 'auto',
                            transition: [
                                'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'border-radius 0.45s ease',
                                'padding 0.4s ease',
                                'background 0.45s ease',
                                'opacity 0.6s ease',
                            ].join(', '),
                        }}
                        onClick={
                            !showSubtitleDisplay
                                ? () => setShowSubtitleDisplay(true)
                                : undefined
                        }
                    >
                        {/* Pill content — visible when collapsed */}
                        <div
                            style={{
                                gridArea: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#2f2e65',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                whiteSpace: 'nowrap',
                                opacity: !showSubtitleDisplay ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0.25s'
                                    : '0s',
                                pointerEvents: 'none',
                            }}
                        >
                            <BsStars size={20} style={{ color: '#2f2e65' }} />
                            {aiVoiceover
                                ? 'AI is speaking...'
                                : 'Click to expand'}
                        </div>
                        {/* Subtitle text — visible when expanded */}
                        <div
                            style={{
                                gridArea: '1/1',
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                opacity: !showSubtitleDisplay ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0s'
                                    : '0.25s',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                        >
                            {aiVoiceover
                                ? typewriterText
                                : currentStepData.instruction}
                        </div>
                    </div>
                )}

                <model-viewer
                    key={modelPath} // Force reload when model changes
                    ref={modelViewerRef}
                    src={modelPath}
                    animation-name={currentAnimationName}
                    animation-crossfade-duration="0"
                    camera-controls
                    shadow-intensity="1"
                />

                {/* Materials panel for the current step */}
                {currentStepData?.materials && (
                    <div
                        className={`materials-panel ${showMaterials ? 'visible' : ''}`}
                    >
                        {Object.entries(currentStepData.materials).map(
                            ([materialName, quantity]) => (
                                <div
                                    key={materialName}
                                    className="material-item"
                                >
                                    <div className="material-image-wrapper">
                                        <img
                                            src={getBackendImage(`${materialName}.png`)}
                                            alt={materialName}
                                            className="material-image"
                                            onError={(e) => {
                                                e.target.style.display = 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="material-label">
                                        {materialName.replace(/_/g, ' ')} x{' '}
                                        {quantity}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}

                {!isShellMode && (
                    <div className="toolbar-sidebar">
                        <button
                            className={`toolbar-button ${showMaterials ? 'active' : ''}`}
                            title="Materials"
                            onClick={handleMaterialsToggle}
                        >
                            <FaShapes />
                        </button>
                        <button
                            className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                            title="AI Voiceover"
                            onClick={() => {
                                handleAiVoiceoverToggle()
                                setSubtitleVisible(true)
                                setShowSubtitleDisplay(true)
                            }}
                        >
                            <BsStars />
                        </button>

                        <button
                            className="toolbar-button"
                            title="Fullscreen"
                            onClick={handleFullscreen}
                        >
                            <MdFullscreen />
                        </button>

                        <button
                            className="toolbar-button"
                            title="Reset"
                            onClick={resetTutorial}
                        >
                            <RxReset />
                        </button>
                    </div>
                )}

                <audio ref={audioRef} />
            </main>

            {/* FOOTER — hidden in shell mode */}
            {!isShellMode && (
                <footer className="pdx-footer">
                    <div className="step-counter">
                        Step {currentStep} of {totalSteps}
                    </div>

                    <div className="step-title">{currentStepData?.title}</div>

                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={handlePrev}
                            // disabled={currentStep === 1}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>

                        <button
                            className="nav-btn"
                            onClick={handleNext}
                            disabled={currentStep === totalSteps && !dataProp}
                        >
                            <span className="footer-btn-text">
                                {currentStep === totalSteps
                                    ? 'Continue'
                                    : 'Next'}
                            </span>
                            <GrNext />
                        </button>
                    </div>
                </footer>
            )}
        </div>
    )
}

export default SimulateNew
